import React, { useState, useEffect, useRef } from 'react';
import PatternGrid from './PatternGrid';
import { loadSamples, playGuitarStrum, playMetronome } from './useAudio';

function buildNotesStr(rhythmNum, rhythmDen, noteLength, totalSteps) {
	const stepsPerBeat = Math.max(1, Math.round(noteLength / rhythmDen));
	const notes = new Array(totalSteps).fill('');
	for (let i = 0; i < rhythmNum; i++) {
		const beatStart = Math.round(i * stepsPerBeat);
		if (beatStart < totalSteps) notes[beatStart] = String(i + 1);
		if (stepsPerBeat >= 2) {
			const half = Math.floor(stepsPerBeat / 2);
			if (beatStart + half < totalSteps) notes[beatStart + half] = '&';
		}
	}
	return notes;
}

const MIN_BPM = 40;
const MAX_BPM = 300;

function requestedStorageKey(songId) {
	return 'strumming_requested_' + songId;
}
function hasRequestedLocally(songId) {
	try { return localStorage.getItem(requestedStorageKey(songId)) === '1'; } catch { return false; }
}
function markRequestedLocally(songId) {
	try { localStorage.setItem(requestedStorageKey(songId), '1'); } catch { /* ignore */ }
}

const PlayIcon = () => (
	<svg width="28" height="28" viewBox="0 0 24 24">
		<circle cx="12" cy="12" r="11" fill="white" />
		<polygon points="10 8 16 12 10 16 10 8" fill="#1e1e1e" />
	</svg>
);
const StopIcon = () => (
	<svg width="28" height="28" viewBox="0 0 24 24">
		<circle cx="12" cy="12" r="11" fill="white" />
		<rect x="9" y="9" width="6" height="6" fill="#1e1e1e" />
	</svg>
);

const MetronomeIcon = () => (
	<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<path d="M9 4h6l4 17H5l4-17z" />
		<path d="M12 18L15 6" />
		<circle cx="13.5" cy="12" r="2.5" fill="currentColor" stroke="none" />
	</svg>
);

const RequestIcon = () => (
	<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<path d="M4 4h16v12H7l-3 3V4z" />
	</svg>
);

export default function StrummingDisplay({ patterns: initialPatterns, songId, trans = {} }) {
	const [patterns, setPatterns] = useState(initialPatterns || []);
	const [activePatternIdx, setActivePatternIdx] = useState(0);
	const [isPlaying, setIsPlaying] = useState(false);
	const [activeIndex, setActiveIndex] = useState(-1);
	const [metronomeEnabled, setMetronomeEnabled] = useState(false);
	const [bpmOverride, setBpmOverride] = useState(null);
	const [bpmInput, setBpmInput] = useState(() => String(Number((initialPatterns || [])[0]?.bpm) || 100));
	const [requested, setRequested] = useState(() => hasRequestedLocally(songId));
	const [requestStatus, setRequestStatus] = useState(null);
	const intervalRef = useRef(null);
	const idxRef = useRef(-1);
	const patternStrRef = useRef([]);
	const metronomeRef = useRef(false);
	const stepsPerBeatRef = useRef(2);

	useEffect(() => { metronomeRef.current = metronomeEnabled; }, [metronomeEnabled]);

	useEffect(() => { loadSamples(); }, []);

	useEffect(() => {
		const handler = e => setPatterns(e.detail);
		window.addEventListener('strummingPatternsSaved', handler);
		return () => window.removeEventListener('strummingPatternsSaved', handler);
	}, []);

	function stopPlayback() {
		clearInterval(intervalRef.current);
		idxRef.current = -1;
		setIsPlaying(false);
		setActiveIndex(-1);
	}

	useEffect(() => () => clearInterval(intervalRef.current), []);

	function handleRequest() {
		if (requested) {
			setRequestStatus(trans.request_already);
			return;
		}
		setRequested(true);
		markRequestedLocally(songId);
		setRequestStatus(trans.request_thanks);

		fetch('api/request-strumming-pattern.html', {
			method: 'POST',
			body: new URLSearchParams({ song_id: songId }),
		}).catch(() => { /* best-effort */ });
	}

	if (patterns.length === 0) {
		return (
			<div className="sp-display-wrap sp-empty-state">
				<div className="sp-empty-text">{trans.empty_state}</div>
				<button className="sp-request-btn" onClick={handleRequest}>
					<RequestIcon />
					<span>{trans.request_button}</span>
				</button>
				{requestStatus && <div className="sp-request-status">{requestStatus}</div>}
			</div>
		);
	}

	const pattern = patterns[activePatternIdx] ?? patterns[0];

	const patternStr = Array.isArray(pattern.pattern) ? pattern.pattern : JSON.parse(pattern.pattern);
	const [rhythmNum, rhythmDen] = pattern.rhythm.split('/').map(Number);
	const noteLength = pattern.note_length || 8;
	const stepsPerBeat = Math.max(1, Math.round(noteLength / rhythmDen));
	const notesStr = buildNotesStr(rhythmNum, rhythmDen, noteLength, patternStr.length);
	const bpm = bpmOverride ?? (Number(pattern.bpm) || 100);
	const intervalMs = (60 / bpm) / stepsPerBeat * 1000;

	patternStrRef.current = patternStr;
	stepsPerBeatRef.current = stepsPerBeat;

	function tick() {
		idxRef.current = (idxRef.current + 1) % patternStrRef.current.length;
		setActiveIndex(idxRef.current);
		const dir = patternStrRef.current[idxRef.current];
		if (dir) playGuitarStrum(dir);
		if (metronomeRef.current && idxRef.current % stepsPerBeatRef.current === 0) {
			playMetronome(idxRef.current === 0);
		}
	}

	function switchPattern(i) {
		stopPlayback();
		setActivePatternIdx(i);
		setBpmOverride(null);
		setBpmInput(String(Number(patterns[i].bpm) || 100));
	}

	function commitBpm(newBpm) {
		const clamped = Math.min(MAX_BPM, Math.max(MIN_BPM, newBpm));
		setBpmOverride(clamped);
		setBpmInput(String(clamped));

		if (isPlaying) {
			clearInterval(intervalRef.current);
			const newIntervalMs = (60 / clamped) / stepsPerBeatRef.current * 1000;
			intervalRef.current = setInterval(tick, newIntervalMs);
		}
	}

	function adjustBpm(delta) {
		commitBpm(bpm + delta);
	}

	function togglePlay() {
		if (isPlaying) { stopPlayback(); return; }

		setIsPlaying(true);
		idxRef.current = -1;

		tick();
		intervalRef.current = setInterval(tick, intervalMs);
	}

	return (
		<div className="sp-display-wrap">
			{patterns.length > 1 && (
				<div className="sp-tabs">
					{patterns.map((p, i) => (
						<button
							key={p.id ?? i}
							className={'sp-tab' + (i === activePatternIdx ? ' sp-tab-active' : '')}
							onClick={() => switchPattern(i)}
						>
							{p.name}
						</button>
					))}
				</div>
			)}
			<div className="sp-player">
				<div className="sp-player-header">
					<button className="sp-play-btn" onClick={togglePlay}>
						{isPlaying ? <StopIcon /> : <PlayIcon />}
					</button>
					<div className="sp-tempo">
						<button className="sp-tempo-step-btn" onClick={() => adjustBpm(-1)} title="-1 bpm">−</button>
						<input
							type="number"
							className="sp-tempo-input"
							min={MIN_BPM}
							max={MAX_BPM}
							value={bpmInput}
							onChange={e => setBpmInput(e.target.value)}
							onBlur={() => {
								const parsed = parseInt(bpmInput, 10);
								commitBpm(Number.isFinite(parsed) ? parsed : bpm);
							}}
							onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
						/>
						<button className="sp-tempo-step-btn" onClick={() => adjustBpm(1)} title="+1 bpm">+</button>
						<span className="sp-tempo-label">bpm</span>
					</div>
					<button
						className={'sp-display-metronome-btn' + (metronomeEnabled ? ' sp-metronome-on' : '')}
						onClick={() => setMetronomeEnabled(v => !v)}
						title="Vklopi/izklopi metronom"
					>
						<MetronomeIcon />
					</button>
				</div>
				<PatternGrid
					patternStr={patternStr}
					notesStr={notesStr}
					rhythmNum={rhythmNum}
					rhythmDen={rhythmDen}
					noteLength={noteLength}
					activeIndex={activeIndex}
					focusedIndex={-1}
					isEditable={false}
				/>
			</div>
		</div>
	);
}
